/* eslint react/require-default-props: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import Carousel from 'components/Carousel';
import { SITE_PLATFORM_MOBILE } from 'helpers/BreakpointHelper';
import {
  isClip,
  isEpisode,
  isUngatedVideo,
  NAME_RECOMMENDED_SERIES,
  NATION_WATCH_FREE,
  isSeriesCollection,
} from 'helpers/ContentHelper';
import {
  helperGetRelativeUrl,
  redirectToExternalUrl,
  helperGetStaticContentUrl,
} from 'helpers/RouteHelper';
import { getAnonymousCTAText } from 'helpers/SeriesDetailHelpers';
import {
  clipsVideoProcessor,
  seriesCollectionVideoProcessor,
  seriesVideoProcessor,
} from 'helpers/VideoProcessorHelper';
import CarouselPosterTheme from 'screens/ContentDetail/styles/CarouselPoster.module.scss';
import CarouselTheme from 'screens/ContentDetail/styles/SeriesDetailsCarousel.css';
import CarouselTileTheme from 'screens/ContentDetail/styles/SeriesDetailsCarouselTile.css';
import { BREAKPOINTS } from 'screens/ContentDetail/ContentDetailHelper';
import { nationUngatedCTAEnabled } from 'helpers/ConfigHelper';
import style from 'screens/ContentDetail/styles/SeriesDetail.css';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';

const fallbackImage = helperGetStaticContentUrl('images', 'FallBack-FOXNATION-min.jpg');
const fallbackPoster = helperGetStaticContentUrl('images', 'FallBack-FOXNATION-Poster.jpg');

const CarouselSection = ({
  bookmarks,
  breakpoint,
  collectionCount,
  inline = {},
  isAnonymous,
  isPosters,
  isPersonality,
  isSeasons,
  isSeriesDetailAnonymous = true,
  dropdownHeadline,
  panel,
  promos = [],
  overrideAdditionalPosterStyle,
  renderDivider = false,
  seriesType = '',
  showUpgradeCTA,
  verticalPosition,
}) => {
  const {
    addCarouselPlaceholders,
    allowScrollLock,
    applyInlineBackground,
    carouselLimit,
    posterLimit,
    posterToScroll,
    roundArrows,
    slidesToScroll,
    showCarouselArrows,
    showTileMetadataOnHover,
    subtitleLines,
    truncateSubtitle,
    useScrollableDiv,
  } = BREAKPOINTS[breakpoint];
  const { id, name } = panel;
  const headline = dropdownHeadline || _.get(panel, 'headline', name);
  const items = _.get(panel, 'items.member') || _.get(panel, 'member');
  const promoContent = _.get(promos, '3.items.member', []);
  const promoCTA = _.get(promoContent, '0.ctaText', '');
  const promoLink = _.get(promoContent, '0.url', '');
  let slidesToShow = isPosters ? posterLimit : carouselLimit;
  if (!inline || !items) return null;

  const carouselProcessor = item => {
    const { '@id': id, displaySeasonAndEpisodeCounts } = item;
    const isMobile = breakpoint === SITE_PLATFORM_MOBILE;
    const getProcessor = ({ seriesType, videoType }) => {
      if (isSeriesCollection(seriesType)) return seriesCollectionVideoProcessor;
      switch (videoType) {
        case isEpisode(videoType):
          return seriesVideoProcessor;
        case isClip(videoType):
          return clipsVideoProcessor;
        default:
          return seriesVideoProcessor;
      }
    };
    const url = isPosters
      ? helperGetRelativeUrl({ url: item.relativeUrl })
      : helperGetRelativeUrl({ url: item.playerScreenUrl });
    const processor = getProcessor({ seriesType, videoType: item.videoType });
    const anonymousCTAText = nationUngatedCTAEnabled() ? getAnonymousCTAText({ item }) : promoCTA;
    const isNationWatchFree = id.includes(NATION_WATCH_FREE);
    let subtitle = (!isMobile || isSeasons) && item.description;

    if (isNationWatchFree) {
      subtitle = item.name;
      if (displaySeasonAndEpisodeCounts) {
        const seasonNumFormat = item.seasonNumber ? `S${item.seasonNumber}` : '';
        const episodeNumFormat = item.episodeNumber ? ` E${item.episodeNumber}` : '';
        subtitle = `${seasonNumFormat}${episodeNumFormat} ${item.name}`;
      }
    }

    return {
      ...processor({ bookmarks, item, isPosters }),
      ...(isMobile && !isSeasons && { metadata: null }),
      anonymousCTAText,
      anonymousCTAClick: () => {
        handleAnalytics({
          event: Events.PURCHASE_CTA_CLICKED,
          payload: {
            page_item_title: item.name,
            page_item_subtitle: item.description,
            page_action_location: 'anonymous series detail',
            page_button_text: anonymousCTAText,
          },
        });
        if (promoLink) {
          redirectToExternalUrl(
            nationUngatedCTAEnabled() && isUngatedVideo(item) ? url : promoLink,
          );
        }
      },
      collectionLength: items.length,
      currentSeason: _.get(item, 'currentSeason', ''),
      image: _.get(item, 'images.videoList.raw', fallbackImage),
      imageAltText: _.get(item, 'images.videoList.altText', ''),
      isAnonymous,
      isPlayable: !isAnonymous,
      link: url || promoLink,
      onTileClick: () => {},
      posterImage: _.get(item, 'images.dcg-mark-poster.raw', fallbackPoster),
      posterAltText: _.get(item, 'images.dcg-mark-poster.altText', ''),
      seriesName: _.get(item, 'seriesName', ''),
      subtitle,
      target: '_self',
      titleLines: 0,
    };
  };

  const isRecommended = panel.name === NAME_RECOMMENDED_SERIES;
  const createTheme = isPosters =>
    isPosters && !isRecommended ? CarouselPosterTheme : CarouselTheme;

  const responsive = [
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    },
    {
      breakpoint: 1023,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    },
    {
      breakpoint: 1439,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 4,
      },
    },
    {
      breakpoint: 2559,
      settings: {
        slidesToShow: 5,
        slidesToScroll: 5,
      },
    },
    {
      breakpoint: 2560,
      settings: {
        slidesToShow: 5,
        slidesToScroll: 5,
      },
    },
  ];

  if (
    window.innerWidth >= responsive[1].breakpoint &&
    window.innerWidth <= responsive[2].breakpoint
  ) {
    slidesToShow = responsive[2]?.settings?.slidesToShow;
  }

  const carouselProps = {
    addCarouselPlaceholders,
    allowScrollLock,
    applyInlineBackground,
    collectionCount,
    breakpoint,
    disableHoverStateActionButtons: true,
    headline,
    isAnonymous,
    isSeriesDetailAnonymous,
    isSeriesDetailPage: true,
    isInfinite: false,
    isPersonality,
    isPosters,
    isSeasons,
    items,
    processor: carouselProcessor,
    overrideAdditionalPosterStyle,
    responsive,
    roundArrows,
    showCarouselArrows,
    showTileMetadataOnHover,
    showUpgradeCTA,
    slidesToScroll: isPosters ? posterToScroll : slidesToScroll,
    slidesToShow,
    subtitleLines,
    truncateSubtitle,
    theme: createTheme(isPosters),
    tileTheme: CarouselTileTheme,
    useScrollableDiv,
    verticalPosition,
  };

  const carouselContainerClass = classNames(
    !isSeasons ? style.carouselContainer : '',
    isPersonality && !isAnonymous ? style.personalityCarouselContainer : '',
  );

  return (
    <div key={id} className={!isSeasons ? style.section : ''}>
      <div className={carouselContainerClass}>
        <Carousel {...carouselProps} />
      </div>
      {renderDivider && (
        <div className={style.dividerContainer}>
          <div className={style.carouselDivider} />
        </div>
      )}
    </div>
  );
};

CarouselSection.propTypes = {
  bookmarks: PropTypes.shape({}),
  breakpoint: PropTypes.string.isRequired,
  collectionCount: PropTypes.number,
  disableScroll: PropTypes.func,
  dropdownHeadline: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  inline: PropTypes.shape({}),
  isAnonymous: PropTypes.bool.isRequired,
  isPersonality: PropTypes.bool,
  isPosters: PropTypes.bool.isRequired,
  isSeriesDetailAnonymous: PropTypes.bool,
  isSeasons: PropTypes.bool,
  panel: PropTypes.shape({}).isRequired,
  promos: PropTypes.arrayOf(PropTypes.shape({})),
  overrideAdditionalPosterStyle: PropTypes.bool,
  renderDivider: PropTypes.bool,
  seriesType: PropTypes.string,
  showUpgradeCTA: PropTypes.bool,
  verticalPosition: PropTypes.number,
};

export default CarouselSection;
